import { useRef } from "react";
import { Link } from "react-router-dom";
import { PioneerHoloCard } from "@shared/components/PioneerHoloCard";
import { cn } from "@shared/lib/utils";
import type { AgentCardItem } from "@/lib/api";
import { cardArtSrc } from "@/lib/cardArt";
import type { CardFinishMeta } from "@/lib/cards";
import { fmt } from "@/lib/format";
import type { HomeAgent } from "@/lib/homeAgents";
import { mockCardIp } from "@/lib/renewal/cardActions";
import { SUPPLY_TEXT, itemSupplyStatus } from "@/lib/supplyStatus";
import { supplyLine } from "./supply";

/** Everything the page-level popover needs to enlarge a card from its tile. */
export type CardSelection = {
  agent: HomeAgent;
  meta: CardFinishMeta;
  item: AgentCardItem;
  originRect: DOMRect | null;
};

type FinishCardTileProps = {
  agent: HomeAgent;
  meta: CardFinishMeta;
  item: AgentCardItem;
  /** Catalog № — stable dex number, kept regardless of sort (§2C). */
  number: number;
  onSelect: (selection: CardSelection) => void;
};

/**
 * Owned finish tile — Courtyard tile grammar (§2), fixed order:
 * ① image (overlay ≤1: ×n count; rarity ring lives on the card art)
 * ② name 2 lines 14/700 (catalog № as mono prefix, 2-line height reserved)
 * ③ hairline divider
 * ④ bottom row: value left (mono tnum) · supply reference chip right —
 *   value never stands alone (R-21). TODO(server): mockCardIp
 */
export function FinishCardTile({ agent, meta, item, number, onSelect }: FinishCardTileProps) {
  const tileRef = useRef<HTMLButtonElement>(null);

  return (
    <article className="flex flex-col gap-2 overflow-visible">
      <button
        ref={tileRef}
        type="button"
        aria-label={`View ${agent.name} — ${meta.label}`}
        onClick={() =>
          onSelect({
            agent,
            meta,
            item,
            originRect: tileRef.current?.getBoundingClientRect() ?? null,
          })
        }
        className={cn(
          "group relative w-full cursor-pointer overflow-visible border-0 bg-transparent p-0 text-left",
          "transition duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent/70",
        )}
      >
        <PioneerHoloCard
          // 실카드 아트 우선 — 미존재 조합은 기존 초상 폴백
          imageSrc={cardArtSrc(agent.agentId, meta.finish) ?? agent.portrait}
          imageAlt={`${agent.name} — ${meta.label}`}
          effect={meta.holoEffect}
          muted
          idleOpacityScale={0.55}
          loading="lazy"
        />
        {item.count > 1 ? (
          <span className="pointer-events-none absolute right-1.5 top-1.5 z-10 rounded-full border border-line bg-black/70 px-2 py-0.5 font-mono text-[9px] font-medium tabular-nums text-white/85">
            ×{item.count}
          </span>
        ) : null}
      </button>

      <div className="px-0.5">
        <p className="line-clamp-2 min-h-10 text-sm font-bold leading-5 text-ink">
          <span className="font-mono text-[10px] font-medium tabular-nums text-ink-mute">
            #{String(number).padStart(2, "0")}
          </span>{" "}
          {meta.label}
        </p>
        <div className="mt-1.5 border-t border-line" aria-hidden />
        <div className="mt-1.5 flex items-center justify-between gap-2">
          <p className="font-mono text-xs font-medium tabular-nums text-ink">
            {fmt(mockCardIp(meta.finish))} <span className="font-normal text-ink-mute">IP</span>
          </p>
          <span
            className={cn(
              "shrink-0 rounded-full border border-line bg-white/4 px-2 py-0.5 font-mono text-[10px] font-medium tabular-nums",
              SUPPLY_TEXT[itemSupplyStatus(item) ?? "plentiful"],
            )}
          >
            {supplyLine(meta, item)}
          </span>
        </div>
        <Link
          to={`/cards/${encodeURIComponent(item.id)}`}
          className="mt-1 inline-flex min-h-11 items-center font-mono text-[10px] font-medium uppercase tracking-[0.08em] text-ink-dim underline-offset-2 transition-colors hover:text-ink hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent/70"
          aria-label={`${agent.name} ${meta.label} card details`}
        >
          Details →
        </Link>
      </div>
    </article>
  );
}
